/**
 * Card Registration Callback Page
 * Handles the callback from Flow.cl after customer registers their card
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckoutSession } from '../hooks';
import LoadingSpinner from '../components/LoadingSpinner';

type CallbackStatus = 'processing' | 'success' | 'cancelled' | 'error';

export default function CardRegisterCallbackPage() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>('');

  // Only fetch session when needed
  const { session } = useCheckoutSession(sessionId);

  useEffect(() => {
    // Set language from session
    if (session?.language && session.language !== i18n.language) {
      i18n.changeLanguage(session.language);
    }
  }, [session, i18n]);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract parameters from URL
        // Flow sends: ?session_id=...&token=... (via POST or redirect)
        const params = new URLSearchParams(window.location.search);
        let sessionIdParam = params.get('session_id');
        const token = params.get('token');

        // If no session_id in URL, check session storage
        if (!sessionIdParam) {
          const savedSessionId = sessionStorage.getItem('checkout_session_id');
          sessionIdParam = savedSessionId || '';
        }

        // Validate we have session ID
        if (!sessionIdParam) {
          throw new Error(t('error.missingSessionId') || 'Missing session ID in callback');
        }

        setSessionId(sessionIdParam);

        // If no token, user cancelled
        if (!token) {
          setStatus('cancelled');
          return;
        }

        // We have a token from Flow, call backend to complete registration
        const { default: checkoutApi } = await import('../lib/api');

        const result = await checkoutApi.verifyCardRegistration(sessionIdParam, token);

        // Success! Redirect to success page
        setStatus('success');
        setTimeout(() => {
          window.location.href = `/success/${sessionIdParam}?subscriptionId=${result.subscription_id}`;
        }, 1500);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.cardRegistrationFailed') || 'Card registration failed';
        setError(message);
        setStatus('error');
      }
    };

    // Only process once
    if (status === 'processing') {
      handleCallback();
    }
  }, [status, t, session?.error_code]);

  // Processing state
  if (status === 'processing') {
    return <LoadingSpinner message={t('checkout.processingCardRegistration') || 'Procesando registro de tarjeta...'} />;
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
        <div className="card w-full relative z-10">
          {/* Success Icon & Title */}
          <div className="text-center mb-8">
            <div className="status-icon-container status-icon-success mb-6">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('cardRegistration.success.title') || 'Tarjeta Registrada'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('cardRegistration.success.message') || 'Tu tarjeta se ha registrado correctamente. Creando suscripción...'}
            </p>
          </div>

          {/* Loading spinner */}
          <div className="py-8">
            <LoadingSpinner message={t('checkout.processingCardRegistration') || 'Procesando registro de tarjeta...'} />
          </div>
        </div>
      </div>
    );
  }

  // Cancelled state
  if (status === 'cancelled') {
    return (
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
        <div className="card w-full relative z-10">
          {/* Cancelled Icon & Title */}
          <div className="text-center mb-8">
            <div className="status-icon-container status-icon-warning mb-6">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('cardRegistration.cancelled.title') || 'Registro Cancelado'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('cardRegistration.cancelled.message') || 'Cancelaste el registro de tu tarjeta. Puedes intentar nuevamente.'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => {
                if (sessionId) {
                  window.location.href = `/checkout/${sessionId}`;
                } else {
                  window.history.back();
                }
              }}
              className="btn-primary w-full py-3"
            >
              {t('cardRegistration.cancelled.retryButton') || 'Volver al Checkout'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
        <div className="card w-full relative z-10">
          {/* Error Icon & Title */}
          <div className="text-center mb-8">
            <div className="status-icon-container status-icon-error mb-6">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('error.title') || 'Error'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </div>

          {/* Error details if available */}
          {sessionId && (
            <div className="mb-8 bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 border border-gray-200 dark:border-gray-800">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                {t('cardRegistration.error.sessionInfo') || 'Session ID'}
              </p>
              <p className="font-mono text-xs text-gray-900 dark:text-white break-all">{sessionId}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => {
                // Re-trigger the callback processing
                window.location.reload();
              }}
              className="btn-primary w-full py-3"
            >
              {t('cardRegistration.error.retryButton') || 'Reintentar Registro'}
            </button>
            <button
              onClick={() => {
                if (sessionId) {
                  window.location.href = `/checkout/${sessionId}`;
                } else {
                  window.history.back();
                }
              }}
              className="btn-secondary w-full py-3"
            >
              {t('cardRegistration.error.backButton') || 'Volver al Checkout'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
